// Move work's pure half (#6772, epic #6552; prototype mwItems / mwSelection / itemWhy / mwHTML
// `:1686-1750`): what the panel lists, what a tick covers, what the ticked selection sends, and
// the words of its footer, button and why line. `components/machine-card/MoveWorkPanel.tsx`
// renders it over the server's previews (`lib/move-page.ts`).
//
// NOTHING HERE COMPUTES A LIMIT. Whether an item may move is its OWN preview's source refusal,
// read through `standingRefusal` at the page clock; whether a machine may take the selection
// is the selection preview's. The panel needs no cap of its own on the selection: the card's
// epics are folded from its jobs, so every listed epic covers at least one job here, and epics
// plus uncovered jobs never exceed the machine's jobs, which `RUNNER_MAX_SLOTS_MAX` bounds.
//
// AN EPIC COVERS ITS JOBS ON THIS CARD: ticked, its jobs read ticked and disabled ("with its
// epic") and are not sent again; the selection is `{kind: 'epic'}` per ticked epic plus
// `{kind: 'task'}` per ticked job no ticked epic covers.

import { MOVE_BUDGET_PER_MACHINE } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import type { UtilBox } from './fleetUtilization';
import { boxJobs, capabilityKey, jobState, jobTitle } from './machine-jobs';
import type { MoveItem, MovePreview, MoveRefusalWire } from './move-calls';
import { standingRefusal } from './move-copy';
import type { PreviewState } from './move-page';
import { secondsUntil } from './time';

/** One row of "What to move". */
export type PanelItem = {
  /** Page-unique within the card: `epic:<plan_id>` or `task:<id>`. */
  readonly key: string;
  readonly item: MoveItem;
  /** "Epic <title>" or "<Role> · <task line>". */
  readonly label: string;
  /** The jobs it carries on this card: an epic's count, 1 for a job. */
  readonly jobs: number;
  /** A job's epic key on this card (`epic:<plan_id>`), or null. */
  readonly epicKey: string | null;
};

/**
 * The card's movable items: its epics (one per distinct `plan_id` of its jobs, in the order
 * their first job appears), then its jobs. A last-known job (an offline machine's) is not
 * listed, nor is the epic only it names.
 */
export function panelItems(box: Pick<UtilBox, 'jobs'>, tr: Translator): readonly PanelItem[] {
  const jobs = boxJobs(box).filter((j) => jobState(j) !== 'last_known');
  const epics = new Map<string, PanelItem & { jobs: number }>();
  for (const j of jobs) {
    if (j.epic == null) continue;
    const key = `epic:${j.epic.plan_id}`;
    const seen = epics.get(key);
    if (seen !== undefined) {
      seen.jobs += 1;
      continue;
    }
    epics.set(key, {
      key,
      item: { kind: 'epic', id: j.epic.plan_id },
      label: tr('fleet.move.label_epic', { title: j.epic.title }),
      jobs: 1,
      epicKey: null,
    });
  }
  const tasks: PanelItem[] = jobs.map((j) => ({
    key: `task:${j.id}`,
    item: { kind: 'task', id: j.id },
    label: tr('fleet.move.work_item_job', {
      role: tr(capabilityKey(j.role)),
      title: jobTitle(j, tr),
    }),
    jobs: 1,
    epicKey: j.epic == null ? null : `epic:${j.epic.plan_id}`,
  }));
  return [...epics.values(), ...tasks];
}

/** The job keys a ticked epic covers on this card. */
export function coveredJobs(
  items: readonly PanelItem[],
  ticked: ReadonlySet<string>,
): ReadonlySet<string> {
  return new Set(
    items
      .filter((i) => i.item.kind === 'task' && i.epicKey !== null && ticked.has(i.epicKey))
      .map((i) => i.key),
  );
}

/** What the move sends: each ticked epic, then each ticked job no ticked epic covers. */
export function selectionItems(
  items: readonly PanelItem[],
  ticked: ReadonlySet<string>,
): readonly MoveItem[] {
  const covered = coveredJobs(items, ticked);
  const epics = items.filter((i) => i.item.kind === 'epic' && ticked.has(i.key));
  const tasks = items.filter(
    (i) => i.item.kind === 'task' && ticked.has(i.key) && !covered.has(i.key),
  );
  return [...epics, ...tasks].map((i) => i.item);
}

/** The ticked epics, and every job the move carries (an epic's included). */
export function selectionCounts(
  items: readonly PanelItem[],
  ticked: ReadonlySet<string>,
): { readonly epics: number; readonly jobs: number } {
  const covered = coveredJobs(items, ticked);
  return {
    epics: items.filter((i) => i.item.kind === 'epic' && ticked.has(i.key)).length,
    jobs: items.filter((i) => i.item.kind === 'task' && (ticked.has(i.key) || covered.has(i.key)))
      .length,
  };
}

/** "N epic(s)", "N job(s)" or "N epic(s) and N job(s)". */
export function selectionWhat(
  counts: { readonly epics: number; readonly jobs: number },
  tr: Translator,
): string {
  const epics = tr.plural('fleet.move.work_epics', counts.epics, { count: counts.epics });
  const jobs = tr.plural('fleet.move.work_jobs', counts.jobs, { count: counts.jobs });
  if (counts.epics === 0) return jobs;
  if (counts.jobs === 0) return epics;
  return tr('fleet.move.work_both', { epics, jobs });
}

/** The brand button: "Move <what> to <m>" when it can go, else plain "Move". */
export function moveWorkGoLabel(
  counts: { readonly epics: number; readonly jobs: number },
  targetName: string | null,
  tr: Translator,
): string {
  if (targetName === null || counts.epics + counts.jobs === 0) return tr('fleet.move.button');
  return tr('fleet.move.work_go', { what: selectionWhat(counts, tr), name: targetName });
}

/** The machine's move budget: its row's `left + used`, or the domain's when it reads none. */
export function moveBudget(box: Pick<UtilBox, 'moves'>): number {
  return box.moves == null ? MOVE_BUDGET_PER_MACHINE : box.moves.left + box.moves.used;
}

/** "Uses N move(s) · X of 4 left on <m> this minute": N the preview's cost, X the source's left. */
export function movePanelFooter(
  f: {
    readonly cost: number;
    readonly left: number;
    readonly budget: number;
    readonly name: string;
  },
  tr: Translator,
): string {
  return tr.plural('fleet.move.work_foot', f.cost, {
    count: f.cost,
    left: f.left,
    budget: f.budget,
    name: f.name,
  });
}

/** An item's own short reason (the prototype's itemWhy), else the full sentence. */
export function itemReason(
  r: MoveRefusalWire,
  nowMs: number,
  tr: Translator,
  sentence: (r: MoveRefusalWire) => string,
): string {
  switch (r.reason) {
    case 'item_gone':
      return tr('fleet.move.reason_item_gone');
    case 'still_moving':
      return tr('fleet.move.reason_still_moving');
    case 'waiting_on_answer':
      return tr('fleet.move.reason_waiting');
    case 'still_starting':
      return tr('fleet.move.reason_starting');
    case 'cooling_down': {
      const free = r.free_at == null ? null : Date.parse(r.free_at);
      return tr('fleet.move.reason_cooling', { seconds: secondsUntil(free, nowMs) });
    }
    default:
      return sentence(r);
  }
}

/** An item's own preview's source refusal still standing at `nowMs`; null when it may move. */
export function itemRefusal(
  preview: PreviewState | undefined,
  nowMs: number,
): MoveRefusalWire | null {
  if (preview === undefined || preview.status !== 'ready') return null;
  return standingRefusal(preview.preview.source.refusal, nowMs);
}

/**
 * Drain machine's ticks: every item whose own preview ANSWERED with no source refusal standing
 * at `nowMs`; a job its ticked epic covers rides with the epic and is not ticked on its own. An
 * item not answered yet (pending, failed) is not said to be movable.
 */
export function drainTicks(
  items: readonly PanelItem[],
  previews: ReadonlyMap<string, PreviewState>,
  nowMs: number,
): ReadonlySet<string> {
  const movable = (i: PanelItem): boolean => {
    const p = previews.get(i.key);
    return p !== undefined && p.status === 'ready' && itemRefusal(p, nowMs) === null;
  };
  const ticked = new Set(
    items.filter((i) => i.item.kind === 'epic' && movable(i)).map((i) => i.key),
  );
  for (const i of items) {
    if (i.item.kind !== 'task' || !movable(i)) continue;
    if (i.epicKey !== null && ticked.has(i.epicKey)) continue;
    ticked.add(i.key);
  }
  return ticked;
}

/**
 * The amber why line, or null when the move can go: the page's own pre-send check first
 * (`blockedWhy`; while the guard is locked, its countdown sentence), then the selection
 * preview's standing refusal, then "No machine can take this right now."
 */
export function moveWorkWhy(
  w: {
    readonly blocked: string | null;
    readonly locked: number;
    readonly refusalText: string | null;
    readonly anyTarget: boolean;
  },
  tr: Translator,
): string | null {
  if (w.blocked !== null) {
    return w.locked > 0 ? tr('fleet.machines.ctl_locked', { n: w.locked }) : w.blocked;
  }
  if (w.refusalText !== null) return w.refusalText;
  return w.anyTarget ? null : tr('fleet.move.work_why_no_target');
}

/**
 * What the fleet read says of one item, for its preview: the fields of each job it carries here
 * that a source refusal with no `free_at` is decided on (`status`, `state`, `run_id` and
 * `started_at` for `still_starting` / `still_moving` / `stop_requested`, `pausable` for
 * `lane_not_pausable`), and the machine's `online` and `runner_version` (`cannot_checkpoint`,
 * `capability_off`); `gone` when it carries none. Never `usage`, which moves on every beat. A
 * preview answered for one signature is superseded by a fleet read that shows another (#6772
 * review round 8). The refusals no row field can show clearing are {@link READ_REASKED}.
 */
export function itemSignature(
  box: Pick<UtilBox, 'jobs' | 'online' | 'runner_version'>,
  item: MoveItem,
): string {
  const jobs = boxJobs(box).filter((j) =>
    item.kind === 'task' ? j.id === item.id : j.epic?.plan_id === item.id,
  );
  if (jobs.length === 0) return 'gone';
  const fields = jobs.map((j) =>
    [j.id, j.status, j.state, j.run_id, j.started_at, j.pausable].join(':'),
  );
  return [box.online, box.runner_version, ...fields].join('|');
}

/**
 * The selection's preview, answered at `nowMs`, says `targetId` can take it: no source refusal
 * standing, the source's moves not spent, and that target present with no refusal standing.
 * Such a preview, asked after a send was refused, supersedes that send's refusal.
 */
export function previewConfirms(preview: MovePreview, targetId: string, nowMs: number): boolean {
  if (standingRefusal(preview.source.refusal, nowMs) !== null) return false;
  const { left, free_at } = preview.source;
  if (left < preview.cost && free_at != null && Date.parse(free_at) > nowMs) return false;
  const target = preview.targets.find((x) => x.runner_id === targetId);
  return target !== undefined && standingRefusal(target.refusal, nowMs) === null;
}

/**
 * Refusals that no field of the fleet row clears, so no signature can supersede them (#6772
 * review round 9): `waiting_on_answer` (the question is not on the row) and
 * `transfers_in_flight` (another transfer landing is not on this machine's row, and it can clear
 * before its `free_at`). An answer holding one is asked again on the NEXT fleet read, whatever
 * that read shows, so it is never staler than one fleet poll.
 */
export const READ_REASKED: ReadonlySet<string> = new Set([
  'waiting_on_answer',
  'transfers_in_flight',
]);

/** `r` stands at `nowMs` and is one only a newer fleet read can supersede. */
export function reaskOnRead(r: MoveRefusalWire | null | undefined, nowMs: number): boolean {
  const standing = standingRefusal(r, nowMs);
  return standing !== null && READ_REASKED.has(standing.reason);
}

/** A preview whose source or any target holds a refusal {@link reaskOnRead} names. */
export function previewReaskOnRead(state: PreviewState | null | undefined, nowMs: number): boolean {
  if (state == null || state.status !== 'ready') return false;
  const p = state.preview;
  return (
    reaskOnRead(p.source.refusal, nowMs) || p.targets.some((x) => reaskOnRead(x.refusal, nowMs))
  );
}
